
import type { PropType, SetupContext } from 'vue'
import type { FiltersMessages } from '../../models'
import type { DateRange } from '../../models/domain/date-range'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import { DateRangeUtils } from '../../utils/date-range.utils'
import { DateUtils } from '../../utils/date.utils'
import { EpbButton } from '../shared/epb-button'
import { EpbTextbox } from '../shared/epb-textbox'
import DateRangePickerCalendar from './date-range-picker-calendar.component.vue'

export default defineComponent({
  components: {
    DateRangePickerCalendar,
    EpbButton,
    EpbTextbox,
  },
  props: {
    dateRange: {
      required: true,
      type: Object as PropType<DateRange>,
    },
    messages: {
      required: true,
      type: Object as PropType<FiltersMessages['customDate']>,
    },
    allowFutureSelection: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }: SetupContext) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()

    const baseClass = 'epb-date-range-picker'

    const now = new Date()

    const latestMonthCalendarDate = ref((props.dateRange as DateRange).endDate ?? now)

    const previousMonthCalendarDate = computed((): Date => {
      return DateUtils.addMonths(latestMonthCalendarDate.value, -1)
    })

    const getFirstDateOfMonth = (date: Date): Date =>
      new Date(date.getFullYear(), date.getMonth(), 1)
    const getLastDateOfMonth = (date: Date): Date =>
      new Date(date.getFullYear(), date.getMonth() + 1, 0)

    const navigateToDate = (date: Date): void => {
      latestMonthCalendarDate.value = date
    }

    const shouldNavigateToEndDate = (): boolean => {
      const { startDate, endDate }: DateRange = props.dateRange
      const currentCalendarRange: DateRange = {
        startDate: getFirstDateOfMonth(previousMonthCalendarDate.value),
        endDate: getLastDateOfMonth(latestMonthCalendarDate.value),
      }
      return (
        DateRangeUtils.isDateRangeComplete(props.dateRange) &&
        (!DateRangeUtils.isDateInRange(currentCalendarRange, startDate) ||
          !DateRangeUtils.isDateInRange(currentCalendarRange, endDate))
      )
    }

    watch(
      () => (props.dateRange as DateRange).endDate,
      () => {
        if (DateRangeUtils.isDateRangeEmpty(props.dateRange)) {
          navigateToDate(now)
        } else {
          if (shouldNavigateToEndDate()) {
            navigateToDate((props.dateRange as DateRange).endDate)
          }
        }
      },
    )

    const isNextMonthButtonVisible = computed(() => {
      if (props.allowFutureSelection) {
        return true
      }
      return !DateUtils.isSameMonthAndYear(latestMonthCalendarDate.value, now)
    })

    const isCurrentMonth = (date: Date): boolean => {
      return DateUtils.isSameMonthAndYear(date, now)
    }

    function onDateSelect(date: Date): void {
      emit('date-range-change', DateRangeUtils.updateDateRange(props.dateRange, date))
    }

    function formatDateAsMonth(date: Date): string {
      return DateUtils.formatDateAsMonth(date, $epb.locale)
    }

    function formatDateAsDay(date?: Date): string {
      if (!date) {
        return ''
      }
      return DateUtils.formatDateAsDay(date, $epb.locale)
    }

    function goToNextMonth(): void {
      navigateToDate(DateUtils.addMonths(latestMonthCalendarDate.value, 1))
    }

    function goToPreviousMonth(): void {
      navigateToDate(DateUtils.addMonths(latestMonthCalendarDate.value, -1))
    }

    return {
      baseClass,
      formatDateAsDay,
      formatDateAsMonth,
      goToNextMonth,
      goToPreviousMonth,
      isCurrentMonth,
      isNextMonthButtonVisible,
      latestMonthCalendarDate,
      onDateSelect,
      previousMonthCalendarDate,
    }
  },
})
